import { QueryFunctionContext, useQuery } from "react-query"
import { useCisModule, useTelesalesCustomerNumber } from "@tm/context-distribution"
import { useMemo } from "react"
import { CisCustomerVoucherType } from "@tm/models"
import { showCisOptions } from "./showCisOptions"
import { CisFunctionType, CisFunctionVoucher, ShowCisOptionsRequest } from "./models"

const QUERY_KEY = "cis_useShowCisOptions"

type QueryKey = [typeof QUERY_KEY, string | undefined]

/**
 * Returns the options for the current CIS customer.
 */
export function useShowCisOptions() {
    const { hasCis } = useCisModule()
    const { telesalesCustomerNo, enableServiceCalls } = useTelesalesCustomerNumber()

    const { data, isLoading, status } = useQuery([QUERY_KEY, telesalesCustomerNo], fetchCisOptions, {
        enabled: hasCis && enableServiceCalls,
        staleTime: Infinity,
        cacheTime: Infinity,
    })

    const voucherTypes = useMemo<CisCustomerVoucherType[] | undefined>(() => {
        return data?.cisFunctions
            ?.filter((cisFunction): cisFunction is CisFunctionVoucher => cisFunction.functionType === CisFunctionType.Vouchers)
            .map((cisFunction) => cisFunction.voucherType)
    }, [data])

    return {
        cisOptionsLoading: isLoading,
        cisOptions: data,
        voucherTypes,
        cisOptionsLoaded: status === "error" || status === "success",
        hasErrors: status === "error",
    }
}

function fetchCisOptions({ queryKey: [, customerNo] }: QueryFunctionContext<QueryKey>) {
    const request: ShowCisOptionsRequest = { customerNo }

    return showCisOptions(request)
}
